/**
 * Diagrams panel — lists every diagram in the active branch and lets you
 * switch / rename / change view-kind / delete from a single sidebar.
 *
 * Layout:
 * - Each row shows the diagram's name in the active language and the
 *   resolved view-kind label between brackets, e.g. `Hoofddiagram [Vrij]`.
 * - Clicking a row opens the diagram as a tab and activates it.
 * - Below the active row, an inline detail block shows editable
 *   attributes — bilingual name (translate-on-blur) and view-kind
 *   dropdown — until the row is unselected.
 * - The pin + close-panel buttons mirror the Concept Explorer's UX.
 */
import type { BilingualText, Diagram } from "@ae/domain";
import type { TFunction } from "i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, api } from "../../api/client";
import { getRegistry, resolveBilingual } from "../../registry";
import { useSession } from "../../state/session";
import { useUi } from "../../state/ui";
import { useWorkspaceData } from "../../state/workspace";
import { readHeatMapConfig } from "../canvas/heatMapConfig";
import { PanelCollapseIcon } from "../PanelCollapseIcon";
import { useMultiSelect } from "../useMultiSelect";
import { usePanelWidth } from "../usePanelWidth";
import { RoadmapMatrixConfigBlock } from "./RoadmapMatrixConfigBlock";

export function DiagramsPanel() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const open = useUi((s) => s.diagramsPanelOpen);
  const setOpen = useUi((s) => s.setDiagramsPanelOpen);
  const pinned = useUi((s) => s.diagramsPanelPinned);
  const setPinned = useUi((s) => s.setDiagramsPanelPinned);
  const { width: panelWidth, ResizeHandle } = usePanelWidth(
    "ae.panelWidth.diagramsPanel",
    240,
    "right",
  );
  const workspaceId = useSession((s) => s.workspaceId);
  const branchId = useSession((s) => s.branchId);
  // Alleen kijken: de lijst opent diagrammen; nieuw, wissen en de
  // eigenschappen (naam, type, beschrijving) vallen weg.
  const readOnly = useSession((s) => s.readOnly);
  const revision = useWorkspaceData((s) => s.revision);
  const activeDiagramId = useWorkspaceData((s) => s.activeDiagramId);
  const openDiagramIds = useWorkspaceData((s) => s.openDiagramIds);
  const multi = useMultiSelect();

  const diagrams = useMemo(() => {
    void revision;
    return useWorkspaceData
      .getState()
      .store.listDiagrams()
      .slice()
      .sort((a, b) => resolveBilingual(a.name, lang).localeCompare(resolveBilingual(b.name, lang)));
  }, [revision, lang]);

  // List of selectable view-kinds: every active view in the registry,
  // including custom workspace-scoped types loaded during hydrate.
  const viewKinds = useMemo(() => {
    void revision;
    return getRegistry()
      .listActiveViewKinds()
      .slice()
      .sort((a, b) => resolveBilingual(a.name, lang).localeCompare(resolveBilingual(b.name, lang)));
  }, [lang, revision]);

  // Welke view-kinds al een diagram hebben — voor het verbergen van
  // singleton-view-kinds (Gap/Plateau) uit de Type-picker zodra ze bestaan.
  const existingViewKinds = useMemo(() => new Set(diagrams.map((d) => d.viewKind)), [diagrams]);

  if (!open) return null;

  return (
    <aside
      data-scope="diagrams-panel"
      className="ledger relative flex h-full min-h-0 shrink-0 flex-col border-(--ld-control) border-r bg-(--ld-surface)"
      style={{ width: panelWidth }}
    >
      <header className="flex shrink-0 items-center justify-between border-(--ld-border) border-b px-3 py-2">
        <div className="flex items-baseline gap-2">
          <h2 className="font-semibold text-(--ld-text) text-sm">{t("app.panels.diagrams")}</h2>
          <span className="text-(--ld-muted) text-xs">{diagrams.length}</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            className={`rounded px-1 text-sm leading-none ${
              pinned
                ? "text-(--ld-accent) hover:text-(--ld-accent)"
                : "text-(--ld-muted) hover:text-(--ld-text)"
            }`}
            onClick={() => setPinned(!pinned)}
            title={
              pinned ? t("diagramsPanel.pin.pinnedTitle") : t("diagramsPanel.pin.unpinnedTitle")
            }
            aria-label={pinned ? t("diagramsPanel.pin.unpinAria") : t("diagramsPanel.pin.pinAria")}
            aria-pressed={pinned}
          >
            {pinned ? "📌" : "📍"}
          </button>
          <button
            type="button"
            className="flex h-7 w-7 items-center justify-center rounded text-(--ld-text) text-base leading-none hover:bg-(--ld-active)"
            onClick={() => setOpen(false)}
            title={t("settings.common.close")}
          >
            <PanelCollapseIcon />
          </button>
        </div>
      </header>

      <div
        className="flex shrink-0 items-center gap-2 border-(--ld-border) border-b px-3 py-2"
        hidden={readOnly}
      >
        <button
          type="button"
          className="rounded border border-dashed border-(--ld-control) px-2 py-0.5 text-(--ld-muted) text-xs hover:border-(--ld-muted) hover:text-(--ld-text)"
          onClick={() => {
            if (!workspaceId || !branchId) return;
            if (useSession.getState().readOnly) return;
            const name = window.prompt(t("diagramsPanel.newPrompt"))?.trim();
            if (!name) return;
            const srcLang: "nl" | "en" = lang === "en" ? "en" : "nl";
            const otherLang: "nl" | "en" = srcLang === "nl" ? "en" : "nl";
            const initialName: Record<string, string> = { [srcLang]: name };
            void (async () => {
              try {
                const res = await api.translate({
                  source_lang: srcLang,
                  target_lang: otherLang,
                  text: name,
                  kind: "name",
                });
                initialName[otherLang] = res.text;
              } catch {
                initialName[otherLang] = name;
              }
              void useWorkspaceData
                .getState()
                .createDiagram(workspaceId, branchId, initialName, "archimate.anything");
            })();
          }}
          title={t("diagramsPanel.newTitle")}
        >
          {t("diagramsPanel.newButton")}
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {diagrams.length === 0 ? (
          <p className="px-3 py-4 text-(--ld-muted) text-xs">{t("diagramsPanel.empty")}</p>
        ) : (
          <ul className="flex flex-col">
            {diagrams.map((d) => {
              const isActive = d.id === activeDiagramId;
              const tabOpen = openDiagramIds.includes(d.id);
              const viewKindLabel = viewKindDisplayLabel(d.viewKind, lang, t);
              const inMulti = multi.isSelected(d.id);
              const visibleOrder = diagrams.map((x) => x.id);
              return (
                <li
                  key={d.id}
                  className={`border-(--ld-rule) border-b ${
                    isActive
                      ? "bg-(--ld-active)"
                      : inMulti
                        ? "bg-(--ld-active)/60"
                        : "hover:bg-(--ld-raised)"
                  }`}
                >
                  <button
                    type="button"
                    className="flex w-full items-baseline gap-1 px-3 py-2 text-left text-sm"
                    onClick={(e) => {
                      const mode = multi.handleClick(d.id, e, visibleOrder);
                      if (mode === "single") {
                        if (!workspaceId) return;
                        useWorkspaceData.getState().openDiagram(workspaceId, d.id);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Delete" || e.key === "Backspace") {
                        if (!workspaceId || !branchId) return;
                        if (useSession.getState().readOnly) return;
                        e.preventDefault();
                        const ids =
                          inMulti && multi.size > 1 ? Array.from(multi.selectedIds) : [d.id];
                        // Block deleting all diagrams — keep at least one.
                        if (ids.length >= diagrams.length) {
                          window.alert(t("diagramsPanel.lastDiagramAlert"));
                          return;
                        }
                        const message =
                          ids.length > 1
                            ? t("diagramsPanel.confirmDeleteMany", { n: ids.length })
                            : t("diagramsPanel.confirmDeleteOnePermanent", {
                                name: resolveBilingual(d.name, lang) || d.id,
                              });
                        if (!window.confirm(message)) return;
                        const ws = useWorkspaceData.getState();
                        for (const id of ids) {
                          void ws.deleteDiagram(workspaceId, branchId, id);
                        }
                        multi.clear();
                      }
                    }}
                    title={t("diagramsPanel.rowTitle")}
                  >
                    <span
                      className={`mr-1 inline-block w-1.5 ${
                        tabOpen ? "text-(--ld-positive)" : "text-transparent"
                      }`}
                      aria-hidden
                      title={tabOpen ? t("diagramsPanel.tabOpen") : t("diagramsPanel.tabClosed")}
                    >
                      •
                    </span>
                    <span
                      className={`truncate font-medium ${
                        isActive ? "text-(--ld-accent)" : "text-(--ld-text)"
                      }`}
                    >
                      {resolveBilingual(d.name, lang) || t("diagramsPanel.unnamed")}
                    </span>
                    <span className="ml-1 shrink-0 text-(--ld-muted) text-xs">
                      [{viewKindLabel}]
                    </span>
                  </button>
                  {isActive && workspaceId && branchId && !readOnly && (
                    <ActiveDiagramEditor
                      diagram={d}
                      lang={lang}
                      workspaceId={workspaceId}
                      branchId={branchId}
                      viewKinds={viewKinds}
                      existingViewKinds={existingViewKinds}
                      canDelete={diagrams.length > 1}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <ResizeHandle />
    </aside>
  );
}

/**
 * Editable details for the currently active diagram: bilingual name with
 * translate-on-blur, view-kind picker, and a delete button (gated to the
 * non-last diagram). Sits inline below the row in the list.
 */
function ActiveDiagramEditor({
  diagram,
  lang,
  workspaceId,
  branchId,
  viewKinds,
  existingViewKinds,
  canDelete,
}: {
  diagram: Diagram;
  lang: string;
  workspaceId: string;
  branchId: string;
  viewKinds: ReturnType<typeof getRegistry>["listActiveViewKinds"] extends () => infer R
    ? R
    : never;
  existingViewKinds: Set<string>;
  canDelete: boolean;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="border-(--ld-border) border-t bg-(--ld-raised)">
      <button
        type="button"
        className="flex w-full items-center gap-1 px-3 py-1 text-left text-(--ld-muted) text-xs hover:bg-(--ld-raised)"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        title={expanded ? t("diagramsPanel.properties.hide") : t("diagramsPanel.properties.show")}
      >
        <span className="inline-block w-2.5 text-(--ld-muted)">{expanded ? "▾" : "▸"}</span>
        {t("diagramsPanel.properties.label")}
      </button>
      {expanded && (
        <div className="px-3 pt-1 pb-2">
          <div className="grid grid-cols-2 gap-2">
            <BilingualNameField
              label={t("diagramsPanel.properties.nameNl")}
              fieldLang="nl"
              diagram={diagram}
              counterpart={diagram.name.en ?? ""}
              workspaceId={workspaceId}
              branchId={branchId}
            />
            <BilingualNameField
              label={t("diagramsPanel.properties.nameEn")}
              fieldLang="en"
              diagram={diagram}
              counterpart={diagram.name.nl ?? ""}
              workspaceId={workspaceId}
              branchId={branchId}
            />
          </div>

          {/*
        Bilingual description. When non-empty (in either language) the
        PlantUML emit includes it as a `title "…"` statement at the top
        of the Onderwater venster. Stored on `diagrams.description_json`
        in D1 (added by migration 0007).
      */}
          <div className="mt-2 grid grid-cols-2 gap-2">
            <DiagramDescriptionField
              label={t("diagramsPanel.properties.descriptionNl")}
              fieldLang="nl"
              diagram={diagram}
              counterpart={diagram.description?.en ?? ""}
              workspaceId={workspaceId}
              branchId={branchId}
            />
            <DiagramDescriptionField
              label={t("diagramsPanel.properties.descriptionEn")}
              fieldLang="en"
              diagram={diagram}
              counterpart={diagram.description?.nl ?? ""}
              workspaceId={workspaceId}
              branchId={branchId}
            />
          </div>

          <label className="mt-2 block text-(--ld-muted) text-xs">
            {t("diagramsPanel.properties.type")}
          </label>
          <select
            className="mt-0.5 w-full rounded border border-(--ld-control) bg-(--ld-surface) px-1 py-0.5 text-xs focus:border-(--ld-accent) focus:outline-none"
            value={diagram.viewKind}
            onChange={(e) => {
              void useWorkspaceData
                .getState()
                .changeDiagramViewKind(workspaceId, branchId, diagram.id, e.target.value);
            }}
          >
            {viewKinds.find((vk) => vk.id === diagram.viewKind) === undefined && (
              <option value={diagram.viewKind}>
                {t("diagramsPanel.unknownViewKind", { id: diagram.viewKind })}
              </option>
            )}
            {viewKinds
              .filter((vk) => {
                // Gap/Plateau zijn singletons (één per WP): verberg ze zodra ze
                // al bestaan, behalve als dít diagram zelf dat type al is.
                const isSingleton =
                  vk.layout_mode === "plateau-diff" || vk.layout_mode === "gap-diff";
                if (!isSingleton) return true;
                if (vk.id === diagram.viewKind) return true;
                return !existingViewKinds.has(vk.id);
              })
              .map((vk) => (
                <option key={vk.id} value={vk.id}>
                  {resolveBilingual(vk.name, lang)}
                  {vk.custom ? " ★" : ""}
                </option>
              ))}
          </select>

          <HeatMapConfigBlock
            diagram={diagram}
            workspaceId={workspaceId}
            branchId={branchId}
            lang={lang}
          />

          <RoadmapMatrixConfigBlock
            diagramId={diagram.id}
            workspaceId={workspaceId}
            branchId={branchId}
            lang={lang}
          />

          <div className="mt-2 flex justify-end">
            <button
              type="button"
              className="rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-0.5 text-(--ld-negative) text-xs hover:bg-(--ld-negative-bg) disabled:opacity-40"
              disabled={!canDelete}
              onClick={() => {
                if (!canDelete) return;
                const name = resolveBilingual(diagram.name, lang) || diagram.id;
                if (!window.confirm(t("diagramsPanel.confirmDeleteOne", { name }))) return;
                void useWorkspaceData.getState().deleteDiagram(workspaceId, branchId, diagram.id);
              }}
              title={
                canDelete ? t("diagramsPanel.deleteButton") : t("diagramsPanel.lastDiagramTitle")
              }
            >
              {t("diagramsPanel.deleteButton")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Heat-map-configuratie — drie dropdowns voor view-kinds met
 * `layout_mode = "heat-map"` (analytics.heat-map). Verbergt zichzelf
 * bij andere view-kinds. Eerste dropdown kiest het target (ofwel een
 * gewoon ArchiMate-type ofwel een stereotype); de tweede en derde
 * tonen alleen enum-attributen die op dat target gedefinieerd zijn.
 */
function HeatMapConfigBlock({
  diagram,
  workspaceId,
  branchId,
  lang,
}: {
  diagram: Diagram;
  workspaceId: string;
  branchId: string;
  lang: string;
}) {
  const { t } = useTranslation();
  const reg = getRegistry();
  const vk = reg.getViewKind(diagram.viewKind);
  if (vk?.layout_mode !== "heat-map") return null;

  const config = readHeatMapConfig(diagram.viewState);
  const targetKey: string = config.targetStereotypeId
    ? `stereotype:${config.targetStereotypeId}`
    : config.targetTypeId
      ? `type:${config.targetTypeId}`
      : "";

  // Build target-options: all active element-types + all registered stereotypes.
  const elementTypes = reg.listActiveElementTypes();
  const stereotypes = reg.listStereotypes();

  // Resolve attribute schemas for the current target — used to populate the two
  // axis-dropdowns. Only enum attributes are listed (heat-map plots discrete values).
  let attrSchemas: { name: string; type: string; label: BilingualText; options?: string[] }[] = [];
  if (config.targetStereotypeId) {
    const st = reg.getStereotype(config.targetStereotypeId);
    attrSchemas = [...(st?.attributes ?? [])];
    const applies = st ? reg.appliesToTypeIds(st) : [];
    const baseId = applies.find((id) => id !== "*");
    if (baseId) {
      const baseType = reg.getElementType(baseId);
      if (baseType?.attributes) attrSchemas.push(...baseType.attributes);
    }
  } else if (config.targetTypeId) {
    const et = reg.getElementType(config.targetTypeId);
    attrSchemas = [...(et?.attributes ?? [])];
  }
  const enumAttrs = attrSchemas.filter(
    (a) => (a.type === "enum" || a.type === "enum?") && (a.options?.length ?? 0) > 0,
  );

  const update = (next: import("@ae/domain").HeatMapConfig) => {
    useWorkspaceData.getState().setHeatMapConfig(workspaceId, branchId, diagram.id, next);
  };

  return (
    <div className="mt-3 rounded border border-(--ld-border) bg-(--ld-raised) px-2 py-2">
      <div className="text-xs font-semibold text-(--ld-muted)">
        {t("diagramsPanel.heatMap.title")}
      </div>

      <label className="mt-2 block text-(--ld-muted) text-xs">
        {t("diagramsPanel.heatMap.target")}
      </label>
      <select
        className="mt-0.5 w-full rounded border border-(--ld-control) bg-(--ld-surface) px-1 py-0.5 text-xs focus:border-(--ld-accent) focus:outline-none"
        value={targetKey}
        onChange={(e) => {
          const v = e.target.value;
          if (!v) {
            update({ verticalAttribute: undefined, horizontalAttribute: undefined });
            return;
          }
          if (v.startsWith("stereotype:")) {
            update({
              targetStereotypeId: v.slice("stereotype:".length),
              verticalAttribute: undefined,
              horizontalAttribute: undefined,
            });
          } else if (v.startsWith("type:")) {
            update({
              targetTypeId: v.slice("type:".length),
              verticalAttribute: undefined,
              horizontalAttribute: undefined,
            });
          }
        }}
      >
        <option value="">{t("diagramsPanel.heatMap.choose")}</option>
        <optgroup label="Stereotypes">
          {stereotypes
            .slice()
            .sort((a, b) =>
              resolveBilingual(a.label, lang).localeCompare(resolveBilingual(b.label, lang)),
            )
            .map((s) => (
              <option key={`stereo-${s.id}`} value={`stereotype:${s.id}`}>
                «{resolveBilingual(s.label, lang)}»
              </option>
            ))}
        </optgroup>
        <optgroup label="Types">
          {elementTypes
            .slice()
            .sort((a, b) =>
              resolveBilingual(a.label, lang).localeCompare(resolveBilingual(b.label, lang)),
            )
            .map((t) => (
              <option key={`type-${t.id}`} value={`type:${t.id}`}>
                {resolveBilingual(t.label, lang)}
              </option>
            ))}
        </optgroup>
      </select>

      <label className="mt-2 block text-(--ld-muted) text-xs">
        {t("diagramsPanel.heatMap.vertical")}
      </label>
      <select
        className="mt-0.5 w-full rounded border border-(--ld-control) bg-(--ld-surface) px-1 py-0.5 text-xs focus:border-(--ld-accent) focus:outline-none disabled:bg-(--ld-active)"
        value={config.verticalAttribute ?? ""}
        disabled={enumAttrs.length === 0}
        onChange={(e) => {
          update({ ...config, verticalAttribute: e.target.value || undefined });
        }}
      >
        <option value="">{t("diagramsPanel.heatMap.chooseEnum")}</option>
        {enumAttrs.map((a) => (
          <option key={a.name} value={a.name}>
            {resolveBilingual(a.label, lang)}
          </option>
        ))}
      </select>

      <label className="mt-2 block text-(--ld-muted) text-xs">
        {t("diagramsPanel.heatMap.horizontal")}
      </label>
      <select
        className="mt-0.5 w-full rounded border border-(--ld-control) bg-(--ld-surface) px-1 py-0.5 text-xs focus:border-(--ld-accent) focus:outline-none disabled:bg-(--ld-active)"
        value={config.horizontalAttribute ?? ""}
        disabled={enumAttrs.length === 0}
        onChange={(e) => {
          update({ ...config, horizontalAttribute: e.target.value || undefined });
        }}
      >
        <option value="">{t("diagramsPanel.heatMap.chooseEnum")}</option>
        {enumAttrs.map((a) => (
          <option key={a.name} value={a.name}>
            {resolveBilingual(a.label, lang)}
          </option>
        ))}
      </select>

      {enumAttrs.length === 0 && targetKey !== "" && (
        <div className="mt-2 text-xs italic text-(--ld-notice-ink)">
          {t("diagramsPanel.heatMap.noEnumAttrs")}
        </div>
      )}
    </div>
  );
}

/**
 * Single-language input on the diagram name. On blur it persists the
 * change AND kicks off a translate request to fill the counterpart
 * language — same UX as `BilingualField` in the attribute panel.
 */
function BilingualNameField({
  label,
  fieldLang,
  diagram,
  counterpart,
  workspaceId,
  branchId,
}: {
  label: string;
  fieldLang: "nl" | "en";
  diagram: Diagram;
  counterpart: string;
  workspaceId: string;
  branchId: string;
}) {
  const { t } = useTranslation();
  const value = diagram.name[fieldLang] ?? "";
  const [draft, setDraft] = useState(value);
  const [translating, setTranslating] = useState(false);
  // Track focus so we only sync the draft from the store when the user
  // ISN'T actively typing — without this, every keystroke triggered a
  // re-render that wrote the old store value back into draft, undoing
  // the edit before the user could finish.
  const focusedRef = useRef(false);
  useEffect(() => {
    if (!focusedRef.current) setDraft(value);
  }, [value]);

  const persistAndMaybeTranslate = async (raw: string) => {
    const trimmed = raw.trim();
    const otherLang: "nl" | "en" = fieldLang === "nl" ? "en" : "nl";
    if (trimmed === (diagram.name[fieldLang] ?? "").trim()) {
      return; // no change
    }
    // Persist the edit immediately so the canvas + tabs reflect it.
    const nextName: BilingualText = { ...diagram.name, [fieldLang]: trimmed };
    await useWorkspaceData.getState().renameDiagram(workspaceId, branchId, diagram.id, nextName);
    if (!trimmed) return;
    // Auto-translate to the other language (last-edit-wins, identical to
    // BilingualField in the attribute panel). Skip when the counterpart
    // wasn't even shown — the user is in single-lang mode.
    setTranslating(true);
    try {
      const res = await api.translate({
        source_lang: fieldLang,
        target_lang: otherLang,
        text: trimmed,
        kind: "name",
      });
      // Re-fetch the current state so a concurrent edit on the OTHER
      // field isn't clobbered.
      const cur = useWorkspaceData.getState().store.getDiagram(diagram.id);
      const liveCounterpart = cur?.name[otherLang] ?? "";
      if (liveCounterpart !== counterpart) return;
      await useWorkspaceData.getState().renameDiagram(workspaceId, branchId, diagram.id, {
        ...nextName,
        [otherLang]: res.text,
      });
    } catch (err) {
      // 412 = no Anthropic sign-in → silent fallback: copy the source text.
      if (err instanceof ApiError && err.status === 412) {
        const cur = useWorkspaceData.getState().store.getDiagram(diagram.id);
        const liveCounterpart = cur?.name[otherLang] ?? "";
        if (liveCounterpart === counterpart) {
          await useWorkspaceData.getState().renameDiagram(workspaceId, branchId, diagram.id, {
            ...nextName,
            [otherLang]: trimmed,
          });
        }
      }
    } finally {
      setTranslating(false);
    }
  };

  return (
    <label className="flex flex-col gap-0.5 text-xs">
      <span className="flex items-center justify-between text-(--ld-text)">
        <span>{label}</span>
        {translating && (
          <span className="text-(--ld-accent) text-xs">{t("diagramsPanel.translating")}</span>
        )}
      </span>
      <input
        type="text"
        className="rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-0.5 text-sm focus:border-(--ld-accent) focus:outline-none"
        value={draft}
        onFocus={() => {
          focusedRef.current = true;
        }}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          focusedRef.current = false;
          void persistAndMaybeTranslate(draft);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        }}
      />
    </label>
  );
}

function viewKindDisplayLabel(viewKindId: string, lang: string, t: TFunction): string {
  const vk = getRegistry().getViewKind(viewKindId);
  if (!vk) return t("diagramsPanel.unknownViewKind", { id: viewKindId });
  return resolveBilingual(vk.name, lang);
}

/**
 * Bilingual description field for a diagram. Persists on blur via
 * `setDiagramDescription` and kicks off a translate-on-blur for the
 * counterpart language — same UX as BilingualNameField above, but with
 * `kind: "description"` so the proxy can pick a translation style suited
 * to longer paragraph text. The counterpart is only filled when it's
 * still empty (or untouched since render) — once the user edits the
 * other field manually we won't clobber it.
 */
function DiagramDescriptionField({
  label,
  fieldLang,
  diagram,
  counterpart,
  workspaceId,
  branchId,
}: {
  label: string;
  fieldLang: "nl" | "en";
  diagram: Diagram;
  counterpart: string;
  workspaceId: string;
  branchId: string;
}) {
  const { t } = useTranslation();
  const value = diagram.description?.[fieldLang] ?? "";
  const [draft, setDraft] = useState(value);
  const [translating, setTranslating] = useState(false);
  // Sync external store changes (translate-completion, other-lang edits,
  // server pushes) into the local draft. We allow the sync as long as
  // the user hasn't actually typed in this focus session — that way,
  // when you click into the EN field while a NL→EN translate is still
  // running, the translated text DOES populate the textarea. We only
  // freeze the draft once `dirtyRef` flips on the first keystroke.
  const focusedRef = useRef(false);
  const dirtyRef = useRef(false);
  useEffect(() => {
    if (!focusedRef.current || !dirtyRef.current) setDraft(value);
  }, [value]);

  const persistAndMaybeTranslate = async (raw: string) => {
    const trimmed = raw.trim();
    const otherLang: "nl" | "en" = fieldLang === "nl" ? "en" : "nl";
    if (trimmed === value.trim()) return; // no change
    // Persist the edit immediately.
    const nextDescription: BilingualText = {
      ...(diagram.description ?? {}),
      [fieldLang]: raw,
    };
    await useWorkspaceData
      .getState()
      .setDiagramDescription(workspaceId, branchId, diagram.id, nextDescription);
    if (!trimmed) return;
    // Auto-translate when the counterpart is empty, OR still equals
    // what we saw at render-time (no concurrent manual edit). This
    // avoids clobbering a description the user has hand-tuned.
    setTranslating(true);
    try {
      const res = await api.translate({
        source_lang: fieldLang,
        target_lang: otherLang,
        text: trimmed,
        kind: "description",
      });
      const cur = useWorkspaceData.getState().store.getDiagram(diagram.id);
      const liveCounterpart = cur?.description?.[otherLang] ?? "";
      if (liveCounterpart && liveCounterpart !== counterpart) return;
      await useWorkspaceData.getState().setDiagramDescription(workspaceId, branchId, diagram.id, {
        ...nextDescription,
        [otherLang]: res.text,
      });
    } catch (err) {
      // 412 = no Anthropic sign-in → silent fallback: leave counterpart as-is.
      if (err instanceof ApiError && err.status === 412) return;
      // Anything else (rate-limit, proxy down, model error) is worth
      // surfacing so the user sees WHY the EN-field stayed empty.
      // eslint-disable-next-line no-console
      console.error("translate(description) failed", err);
    } finally {
      setTranslating(false);
    }
  };

  return (
    <label className="flex flex-col gap-0.5 text-xs">
      <span className="flex items-center justify-between text-(--ld-text)">
        <span>{label}</span>
        {translating && (
          <span className="text-(--ld-accent) text-xs">{t("diagramsPanel.translating")}</span>
        )}
      </span>
      <textarea
        rows={2}
        className="rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-0.5 text-sm leading-snug focus:border-(--ld-accent) focus:outline-none"
        value={draft}
        onFocus={() => {
          focusedRef.current = true;
          // New focus session — draft is in sync with `value`, no edits yet.
          dirtyRef.current = false;
        }}
        onChange={(e) => {
          setDraft(e.target.value);
          dirtyRef.current = true;
        }}
        onBlur={() => {
          focusedRef.current = false;
          // Only persist when the user actually typed — otherwise a
          // bare focus+blur (e.g. tabbing through the form, or clicking
          // in to read the auto-translated result) would clobber the
          // store with the stale empty draft.
          if (dirtyRef.current) {
            void persistAndMaybeTranslate(draft);
          }
          dirtyRef.current = false;
        }}
        placeholder={t("diagramsPanel.properties.descriptionPlaceholder")}
      />
    </label>
  );
}
